import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import {
  girisYap as sdkGiris,
  cikisYap as sdkCikis,
  mevcutKullaniciAl,
  EmanetXHata,
  type AdminGirisIstegi,
  type Kullanici,
} from '@emanetx/sdk';
import { istemci, jetonDepo } from './istemci.js';

interface OturumDurumu {
  yukleniyor: boolean;
  kullanici: Kullanici | null;
  ayarla(k: Kullanici | null): void;
}

export const useOturumStore = create<OturumDurumu>((set) => ({
  yukleniyor: true,
  kullanici: null,
  ayarla: (k) => set({ kullanici: k, yukleniyor: false }),
}));

const OTURUM_ANAHTAR = ['oturum', 'mevcut'] as const;

export function useMevcutOturum() {
  const ayarla = useOturumStore((s) => s.ayarla);
  const jeton = jetonDepo.erisim();
  return useQuery({
    queryKey: OTURUM_ANAHTAR,
    queryFn: async () => {
      if (!jeton) {
        ayarla(null);
        return null;
      }
      try {
        const k = await mevcutKullaniciAl(istemci);
        ayarla(k);
        return k;
      } catch (hata) {
        // Sadece kimlik dogrulama hatalarinda oturumu kapat.
        // Network/CORS/timeout/500 hatalarinda jetonu KORU — sonraki istekte
        // SDK refresh akisi devreye girer veya tarayici baglanti duzelir.
        const kimlikHatasi =
          hata instanceof EmanetXHata &&
          (hata.durum === 401 || hata.durum === 403 || hata.kod === 'yenileme_jeton_yok');
        if (kimlikHatasi) {
          jetonDepo.sil();
          ayarla(null);
        } else {
          // Gecici hata: kullaniciyi mevcut sayfada birak, oturumu silme.
           
          console.warn('[oturum] mevcut kullanici alinamadi (gecici)', hata);
          ayarla(useOturumStore.getState().kullanici);
        }
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useGirisYap() {
  const sorguIstemci = useQueryClient();
  const ayarla = useOturumStore((s) => s.ayarla);
  return useMutation({
    mutationFn: async (giris: AdminGirisIstegi) => {
      const cevap = await sdkGiris(istemci, giris);
      jetonDepo.yaz({
        erisimJeton: cevap.erisimJeton,
        yenilemeJeton: cevap.yenilemeJeton,
      });
      ayarla(cevap.kullanici);
      sorguIstemci.setQueryData(OTURUM_ANAHTAR, cevap.kullanici);
      return cevap.kullanici;
    },
  });
}

export function useCikisYap() {
  const sorguIstemci = useQueryClient();
  const ayarla = useOturumStore((s) => s.ayarla);
  return useMutation({
    mutationFn: async () => {
      const yenileme = jetonDepo.yenileme();
      try {
        await sdkCikis(istemci, yenileme);
      } catch {
        // ignore — yine de lokali temizle
      }
      jetonDepo.sil();
      ayarla(null);
      sorguIstemci.setQueryData(OTURUM_ANAHTAR, null);
      sorguIstemci.clear();
    },
  });
}
